// src/constants/statuses.js
//
// Single source of truth for all status strings used across WigleyHub.
//
// Why: status literals scattered across the codebase create silent bugs at
// scale. A typo in one file ('Waiting on Inake') doesn't fail loudly — it
// just makes the badge or filter behave wrongly. By centralizing, all
// comparisons use the same constant; mistypes fail at import time.
//
// Categories:
//   - DOC_REQUEST_STATUS: lifecycle of a document_request row
//   - TASK_STATUS: lifecycle of a task row
//   - DOC_REQUEST_ITEM_STATUS: lifecycle of a document_request_item row
//
// Conventions:
//   - DOC_REQUEST values are lowercase (matches DB enum/text values)
//   - TASK values are Title Case (matches DB values + display labels)
//   - ITEM values are lowercase (matches DB enum/text values)

export const DOC_REQUEST_STATUS = {
  PENDING:    'pending',     // consultant submitted, NTRG hasn't reviewed
  APPROVED:   'approved',    // NTRG approved + email sent to client
  FULFILLED:  'fulfilled',   // all items delivered, complete
  CANCELLED:  'cancelled',   // withdrawn (by consultant pre-approval) or rejected
};

// Convenience predicates used everywhere
export const DOC_REQUEST_STATUS_OPEN = [DOC_REQUEST_STATUS.PENDING, DOC_REQUEST_STATUS.APPROVED];
export const DOC_REQUEST_STATUS_CLOSED = [DOC_REQUEST_STATUS.FULFILLED, DOC_REQUEST_STATUS.CANCELLED];
export const DOC_REQUEST_STATUS_VALID_FOR_CANCEL = [DOC_REQUEST_STATUS.PENDING];

export const TASK_STATUS = {
  OPEN:                'Open',
  IN_PROGRESS:         'In Progress',
  WAITING_ON_CLIENT:   'Waiting on Client',
  WAITING_ON_INTAKE:   'Waiting on Intake',
  WAITING_ON_CONSULTANT: 'Waiting on Consultant',
  WAITING_ON_TEAM_MEMBER: 'Waiting on Team Member',
  PARTIAL_DELIVERED:   'Partial Delivered',
  READY_TO_DELIVER:    'Ready to Deliver',
  COMPLETE:            'Complete',
  CANCELLED:           'Cancelled',
};

// Statuses a human may set by hand in the status dropdown. The three doc-request DELIVERY states (Waiting on Intake,
// Partial Delivered, Ready to Deliver) are DELIBERATELY excluded — they are set ONLY by the document-request workflow
// (auto-transitions + DocumentIntake), never by hand, and hand-setting them (or hand-leaving them for a lateral waiting
// status) would strand the flow. They remain in STATUS_ORDER + the 4-bucket so auto-set tasks still sort and classify.
export const TASK_STATUS_SETTABLE = [
  TASK_STATUS.OPEN,
  TASK_STATUS.IN_PROGRESS,
  TASK_STATUS.WAITING_ON_CLIENT,
  TASK_STATUS.WAITING_ON_CONSULTANT,
  TASK_STATUS.WAITING_ON_TEAM_MEMBER,
  TASK_STATUS.COMPLETE,
  TASK_STATUS.CANCELLED,
];
// The doc-request-only delivery states — auto-managed, not hand-settable. Used to guard the status dropdown.
export const TASK_STATUS_AUTO_ONLY = [
  TASK_STATUS.WAITING_ON_INTAKE,
  TASK_STATUS.PARTIAL_DELIVERED,
  TASK_STATUS.READY_TO_DELIVER,
];

// ── ONE 4-bucket definition — the KPI cards AND the roster both import these (they used to disagree on 4 statuses).
//   TO DO · ON US  = on our side, queued / not yet actively worked. Waiting-on-Intake is here: it's blocked on OUR
//                    intake team (internal), which is on-us to clear — NOT "waiting on them" (external).
//   IN PROGRESS    = actively being worked by us (Partial Delivered = mid-delivery, still working the rest).
//   WAITING·ON THEM= my task, blocked waiting on an EXTERNAL party (client/consultant) to give me what I need.
//   ON_US          = TO DO ∪ IN PROGRESS — the work whose bandwidth is ours (used for the overdue "counts against me" rule).
// ⛔ MIRRORED IN SQL — supabase/migrations/2026-08-15_staff_workload_counts.sql duplicates these three
// arrays as literal status lists, because tasks.status has no enum, no CHECK and no lookup row: this file
// is the only enforcement. Add or rename a status here and you MUST change that function in the same
// commit, or the Team Workload bubbles, the KPI cards and the productivity report silently disagree with
// every other screen.
export const TASK_STATUS_TODO = [
  TASK_STATUS.OPEN,
  TASK_STATUS.WAITING_ON_INTAKE,
  TASK_STATUS.READY_TO_DELIVER,
];
export const TASK_STATUS_IN_PROGRESS = [
  TASK_STATUS.IN_PROGRESS,
  TASK_STATUS.PARTIAL_DELIVERED,
];
export const TASK_STATUS_WAITING = [
  TASK_STATUS.WAITING_ON_CLIENT,
  TASK_STATUS.WAITING_ON_CONSULTANT,
  TASK_STATUS.WAITING_ON_TEAM_MEMBER,
];
export const TASK_STATUS_ON_US = [...TASK_STATUS_TODO, ...TASK_STATUS_IN_PROGRESS];
export const TASK_STATUS_TERMINAL = [TASK_STATUS.COMPLETE, TASK_STATUS.CANCELLED];

// Workflow order for sorting the Status column — lifecycle, not alphabetical (alpha would put Cancelled first,
// Complete second, which is meaningless). Anything not listed sorts before these (indexOf -1).
export const STATUS_ORDER = [
  TASK_STATUS.OPEN,
  TASK_STATUS.IN_PROGRESS,
  TASK_STATUS.WAITING_ON_INTAKE,
  TASK_STATUS.WAITING_ON_CLIENT,
  TASK_STATUS.WAITING_ON_CONSULTANT,
  TASK_STATUS.WAITING_ON_TEAM_MEMBER,
  TASK_STATUS.PARTIAL_DELIVERED,
  TASK_STATUS.READY_TO_DELIVER,
  TASK_STATUS.COMPLETE,
  TASK_STATUS.CANCELLED,
];

// Statuses where the linked document_request might still receive client
// uploads — useful for the live intake recompute
export const TASK_STATUS_PRE_INTAKE = [
  TASK_STATUS.OPEN,
  TASK_STATUS.IN_PROGRESS,
  TASK_STATUS.WAITING_ON_CLIENT,
];

export const DOC_REQUEST_ITEM_STATUS = {
  PENDING:    'pending',     // not yet provided
  FULFILLED:  'fulfilled',   // received from client
  NA:         'na',          // marked not-applicable by client
  NOT_READY:  'not_ready',   // client promised future date
};

// Tax-bill approval workflow (tax_bills.status) — TS validates & submits → CS approves → transmit.
export const TAX_BILL_STATUS = {
  SUBMITTED:   'submitted',    // TS validated + submitted for CS approval (row created here) — "Processed"
  APPROVED:    'approved',     // CS approved → ready to transmit / file
  REJECTED:    'rejected',     // CS sent back to TS (see rejection_note) — sub-state of Processed
  FLAGGED:     'flagged',      // TS flagged a problem bill (see rejection_note) — sub-state of Processed
  TRANSMITTED: 'transmitted',  // transmittal + bill sent to client (transmit clients)
  FILED:       'filed',        // approved bill filed (non-transmit clients — no TBT/email)
  // ⛔ A BASELINE-YEAR BILL, WHICH NEVER ENTERS THE QUEUE. The client's own figure from before we were
  // engaged: transcribed for its jurisdiction breakdown, with nothing for a TS or CS to check. DERIVED
  // FROM THE YEAR at submit (isBaselineYear), never chosen — a checkbox would be a second source of
  // truth that can disagree with first_year.
  //
  // ⛔ IT IS NOT TERMINAL, deliberately. submitTaxBill's guard blocks only approved/transmitted/filed,
  // so re-scanning a baseline year SUPERSEDES a recorded bill and inserts a fresh one — that is the
  // correction path, and it is why Reopen is not offered on one. Routing it through 'rejected' would
  // send it to a worklist that cannot show it: expected_bills gates on is_represented, so a baseline
  // parcel-year returns no row at all, and the bill would vanish from every screen but the parcel card.
  RECORDED:    'recorded',
};
export const TAX_BILL_STATUS_OPEN = [TAX_BILL_STATUS.SUBMITTED, TAX_BILL_STATUS.APPROVED]; // in-flight (not yet sent)
export const TAX_BILL_STATUS_TERMINAL = [TAX_BILL_STATUS.TRANSMITTED, TAX_BILL_STATUS.FILED]; // done (sent or filed)

// Workflow statuses (Not Started/Outstanding are DERIVED — no stored row). Canonical KEYS match the SQL
// expected_bills.workflow_status output; use TAX_BILL_WORKFLOW_LABEL for display.
export const TAX_BILL_WORKFLOW = { OVERDUE: 'overdue', OUTSTANDING: 'outstanding', NOT_STARTED: 'not_started', PROCESSED: 'processed', REJECTED: 'rejected', APPROVED: 'approved', TRANSMITTED: 'transmitted', FILED: 'filed', RECORDED: 'recorded' };
// 'rejected' = CS returned the bill to the TS (sub-state of processed by stored status, but its own workflow key so
// it leaves Approvals and reappears on the TS Processing worklist).
// ⛔ 'overdue' is DERIVED IN SQL (due_date < current_date), ahead of the 45-day rung — there is no stored
// tax_bills.status for it, so billWorkflowStatus() never returns it. It arrives only from expected_bills /
// pull_parcels_by_state. Any screen filtering workflow_status against an explicit list MUST include it or
// overdue rows vanish silently — the two that do are TaxBillWorklist.js and processingRows below.
// 'recorded' reaches no queue — every queue predicate here is an ALLOW-LIST (byStatus, processingRows,
// approvalRows, the worklist's two filters), so it is excluded by omission rather than by a rule. The label
// exists for the parcel card and for TaxBillApprovals' chip(), which falls back to the grey 'outstanding'
// palette on an unknown key and would otherwise print the bare word 'recorded'.
export const TAX_BILL_WORKFLOW_LABEL = { overdue: 'Overdue', outstanding: 'Outstanding', not_started: 'Not Started', processed: 'Processed', rejected: 'Returned', approved: 'Approved', transmitted: 'Transmitted', filed: 'Filed', recorded: 'Recorded' };
// PARCEL-MODAL CHIP labels — the SAME workflow keys as TAX_BILL_WORKFLOW_LABEL, worded for a reader looking at
// one parcel and asking "where is this bill?" rather than one looking at a queue.
// ⛔ DELIBERATE DIVERGENCE, not drift: the worklist says 'Processed' / 'Approved' (what HAPPENED to the bill);
// the chip says 'At Approval' / 'Awaiting Transmittal' (where it IS SITTING). Same key, same fold —
// billWorkflowStatus() below is the ONLY fold; this is a second LABEL SET, never a second mapping.
// Both 'outstanding' and 'not_started' read 'Processing': from the parcel's point of view they are one state
// (no tax_bills row, the TS still owes it); the 45-day split only matters to the worklist's sort.
// If these two vocabularies ever need to agree, change them together — a bill must never read 'Processed'
// on one screen and 'Returned' on another.
export const TAX_BILL_CHIP_LABEL = {
  overdue: 'Processing', outstanding: 'Processing', not_started: 'Processing', processed: 'At Approval',
  rejected: 'Returned', approved: 'Awaiting Transmittal', transmitted: 'Transmitted', filed: 'Filed',
  recorded: 'Recorded',
};

// Map a RAW stored tax_bills.status (or null) → workflow key. The expected_bills RPC already returns the key;
// use this only when you hold a tax_bills row directly (e.g. CS approval / bill-detail views).
export const billWorkflowStatus = (rawStatusOrNull) => {
  switch (rawStatusOrNull) {
    case TAX_BILL_STATUS.SUBMITTED:
    case TAX_BILL_STATUS.FLAGGED:     return TAX_BILL_WORKFLOW.PROCESSED;
    case TAX_BILL_STATUS.REJECTED:    return TAX_BILL_WORKFLOW.REJECTED;
    case TAX_BILL_STATUS.APPROVED:    return TAX_BILL_WORKFLOW.APPROVED;
    case TAX_BILL_STATUS.TRANSMITTED: return TAX_BILL_WORKFLOW.TRANSMITTED;
    case TAX_BILL_STATUS.FILED:       return TAX_BILL_WORKFLOW.FILED;
    // ⛔ AN EXPLICIT CASE, NOT THE DEFAULT. The default below folds anything unknown to OUTSTANDING —
    // "the TS still owes this bill" — so without this line a recorded bill's parcel-card chip would read
    // 'Processing', which is the dead-end shape (a state naming work no queue will ever show). That the
    // default silently mislabels any FUTURE status this way is a real hazard; it is logged in known-debt,
    // not fixed here, because this path no longer reaches it.
    case TAX_BILL_STATUS.RECORDED:    return TAX_BILL_WORKFLOW.RECORDED;
    case 'not_started':               return TAX_BILL_WORKFLOW.NOT_STARTED;
    default:                          return TAX_BILL_WORKFLOW.OUTSTANDING;
  }
};

// All possible item statuses — useful for filter UIs
export const DOC_REQUEST_ITEM_STATUS_ALL = Object.values(DOC_REQUEST_ITEM_STATUS);
// Statuses where item still owes something to consultant
export const DOC_REQUEST_ITEM_STATUS_OWED = [
  DOC_REQUEST_ITEM_STATUS.PENDING,
  DOC_REQUEST_ITEM_STATUS.NOT_READY,
];

// Color tuples for status pills (RGB triplets matching badge palette)
// Keep these here so all renderers use the same colors. Values match the
// pre-existing TaskEngine.js palette (canonical source).
export const TASK_STATUS_COLORS = {
  [TASK_STATUS.OPEN]:                  '96,165,250',     // blue
  [TASK_STATUS.IN_PROGRESS]:           '251,191,36',     // amber
  [TASK_STATUS.WAITING_ON_CLIENT]:     '167,139,250',    // purple
  [TASK_STATUS.WAITING_ON_INTAKE]:     '249,115,22',     // orange
  [TASK_STATUS.WAITING_ON_CONSULTANT]: '180,0,0',    // gold
  [TASK_STATUS.WAITING_ON_TEAM_MEMBER]: '13,148,136',    // teal
  [TASK_STATUS.PARTIAL_DELIVERED]:     '14,165,233',     // sky
  [TASK_STATUS.READY_TO_DELIVER]:      '167,105,250',    // gold-purple
  [TASK_STATUS.COMPLETE]:              '52,211,153',     // green
  [TASK_STATUS.CANCELLED]:             '107,116,153',    // gray
};
